import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/store/auth'
import { Button, Input } from '@diwata/ironvale'

type Step = 'tier' | 'name' | 'realm' | 'covenant' | 'done'

// The promoted taxonomy (familiar_spec §2). Platform familiars (Mutya, Tala) ship with their
// products and are never created here — the three keeper-creatable tiers are listed.
const TIERS = [
  {
    value: 'sovereign',
    label: 'Sovereign',
    desc: 'Your own familiar and principal, one per keeper. It acts across the suite, delegates to your Adepts, and its command-center app wears its name.',
  },
  {
    value: 'adept',
    label: 'Adept',
    desc: 'A purpose-built specialist you hand a goal: research, drafting, code review. Reached through your Sovereign; the unit of compartmentalization.',
  },
  {
    value: 'guild',
    label: 'Guild',
    desc: 'An organisational familiar owned by a Circle, serving its Realms. Bonded keepers address it; it responds, it does not initiate.',
  },
]

function slugify(v: string): string {
  return v.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

export function CreateFamiliar() {
  const navigate = useNavigate()
  const api = useAuth((s) => s.api)()
  const keeper = useAuth((s) => s.keeper)
  const qc = useQueryClient()

  const [step, setStep] = useState<Step>('tier')
  const [tier, setTier] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState('')
  const [boundaries, setBoundaries] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Guild binding — which Circle the familiar belongs to, which Realm it serves.
  const [circleId, setCircleId] = useState('')
  const [realmId, setRealmId] = useState('')
  const [newRealmName, setNewRealmName] = useState('')
  const [addingRealm, setAddingRealm] = useState(false)
  const [realmSaving, setRealmSaving] = useState(false)
  const [realmError, setRealmError] = useState('')

  const isGuild = tier === 'guild'

  // One Sovereign per keeper (familiar_spec §2) — when one already exists, the tier is taken.
  const { data: familiars = [] } = useQuery({
    queryKey: ['familiars'],
    queryFn: () => api.listFamiliars(),
  })
  const existingSovereign = familiars.find((f) => f.tier === 'sovereign')

  const { data: circles = [], isLoading: circlesLoading } = useQuery({
    queryKey: ['circles'],
    queryFn: () => api.listCircles(),
    enabled: isGuild,
  })
  const { data: members = [] } = useQuery({
    queryKey: ['members', circleId],
    queryFn: () => api.listMembers(circleId),
    enabled: isGuild && !!circleId,
  })
  const { data: realms = [], isLoading: realmsLoading } = useQuery({
    queryKey: ['realms', circleId],
    queryFn: () => api.listRealms(circleId),
    enabled: isGuild && !!circleId,
  })

  // Only a Warden or Elder of the Circle may create its Guild familiars —
  // the server enforces this; the wizard says so before the attempt.
  const myRole = members.find((m) => m.keeper_id === keeper?.id)?.role
  const canSteward = myRole === 'warden' || myRole === 'elder'

  function selectCircle(id: string) {
    setCircleId(id)
    setRealmId('')
    setAddingRealm(false)
    setRealmError('')
  }

  async function createRealmInline() {
    setRealmSaving(true)
    setRealmError('')
    try {
      const realm = await api.createRealm(circleId, newRealmName.trim(), slugify(newRealmName))
      qc.invalidateQueries({ queryKey: ['realms', circleId] })
      setRealmId(realm.id)
      setNewRealmName('')
      setAddingRealm(false)
    } catch (e) {
      setRealmError(String(e))
    } finally {
      setRealmSaving(false)
    }
  }

  async function handleCreate() {
    setLoading(true)
    setError('')
    try {
      const familiar = await api.createFamiliar({
        name,
        tier,
        covenant: {
          role,
          boundaries: boundaries.split('\n').map((s) => s.trim()).filter(Boolean),
        },
        ...(isGuild ? { realm_id: realmId } : {}),
      })
      if (role) await api.activateFamiliar(familiar.id)
      navigate(`/familiar/${familiar.id}/chat`)
    } catch (e) {
      setError(String(e))
      setLoading(false)
    }
  }

  return (
    <div style={{ maxWidth: 520 }}>
      <div className="cp-page-header">
        <div>
          <div className="cp-page-title">Create a familiar</div>
          <div className="cp-page-subtitle">
            {step === 'tier' && "Choose the familiar's tier"}
            {step === 'name' && 'Give your familiar a name'}
            {step === 'realm' && 'Choose the Circle and Realm it serves'}
            {step === 'covenant' && 'Define the Covenant'}
          </div>
        </div>
      </div>

      {step === 'tier' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {TIERS.map((t) => {
            const taken = t.value === 'sovereign' && !!existingSovereign
            return (
              <button
                key={t.value}
                className="cp-familiar-card"
                disabled={taken}
                style={{
                  textAlign: 'left',
                  borderColor: tier === t.value ? 'var(--ae-color-action-primary)' : undefined,
                  borderWidth: tier === t.value ? 'var(--ae-border-width-thick)' : undefined,
                  opacity: taken ? 0.55 : undefined,
                  cursor: taken ? 'not-allowed' : undefined,
                }}
                onClick={() => setTier(t.value)}
              >
                <div className="cp-familiar-name">{t.label}</div>
                <div className="cp-page-subtitle">{t.desc}</div>
                {taken && (
                  <div className="cp-page-subtitle" style={{ marginTop: 6 }}>
                    {existingSovereign.name} already holds this seat. One Sovereign per keeper.
                  </div>
                )}
              </button>
            )
          })}
          <div className="cp-page-subtitle">
            Platform familiars (Mutya, Tala) ship with their products and are never created here.
          </div>
          <Button disabled={!tier} onClick={() => setStep('name')} style={{ marginTop: 8 }}>
            Continue
          </Button>
        </div>
      )}

      {step === 'name' && (
        <div className="cp-form">
          <div className="cp-field">
            <label className="cp-label">Name</label>
            <Input
              placeholder="e.g. Aiden, Vesper, Oryn"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" onClick={() => setStep('tier')}>Back</Button>
            <Button
              disabled={!name.trim()}
              onClick={() => setStep(isGuild ? 'realm' : 'covenant')}
            >
              Continue
            </Button>
          </div>
        </div>
      )}

      {step === 'realm' && (
        <div className="cp-form">
          <div className="cp-page-subtitle" style={{ marginBottom: 4 }}>
            A Guild familiar is owned by a Circle and serves one of its Realms.
            Creating one requires the Warden or Elder role in that Circle.
          </div>

          {circlesLoading && <p className="cp-empty">Loading your Circles…</p>}

          {!circlesLoading && circles.length === 0 && (
            <>
              <p className="cp-empty">
                You are not a member of any Circle yet. Found a Circle for your
                organisation first, then return here to create its familiar.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="ghost" onClick={() => setStep('name')}>Back</Button>
                <Button onClick={() => navigate('/circles')}>Go to Circles</Button>
              </div>
            </>
          )}

          {!circlesLoading && circles.length > 0 && (
            <>
              <div className="cp-field">
                <label className="cp-label">Circle</label>
                <select
                  className="cp-select"
                  value={circleId}
                  onChange={(e) => selectCircle(e.target.value)}
                  autoFocus
                >
                  <option value="">Select a Circle…</option>
                  {circles.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              {circleId && myRole && !canSteward && (
                <p className="cp-error">
                  Your role in this Circle is Member. Only a Warden or Elder can
                  create its Guild familiars — ask one of them to raise your role
                  or to create the familiar.
                </p>
              )}

              {circleId && canSteward && (
                <div className="cp-field">
                  <label className="cp-label">Realm</label>
                  {realmsLoading && <p className="cp-page-subtitle">Loading Realms…</p>}
                  {!realmsLoading && realms.length > 0 && (
                    <select
                      className="cp-select"
                      value={realmId}
                      onChange={(e) => setRealmId(e.target.value)}
                    >
                      <option value="">Select a Realm…</option>
                      {realms.map((r) => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                  )}
                  {!realmsLoading && realms.length === 0 && !addingRealm && (
                    <p className="cp-page-subtitle">
                      This Circle has no Realms yet — create one to continue.
                    </p>
                  )}
                  {!addingRealm ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      style={{ alignSelf: 'flex-start', marginTop: 6 }}
                      onClick={() => setAddingRealm(true)}
                    >
                      + New Realm
                    </Button>
                  ) : (
                    <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                      <Input
                        placeholder="Realm name, e.g. Research Desk"
                        value={newRealmName}
                        onChange={(e) => setNewRealmName(e.target.value)}
                        autoFocus
                      />
                      <Button
                        size="sm"
                        disabled={realmSaving || !newRealmName.trim()}
                        onClick={createRealmInline}
                      >
                        {realmSaving ? 'Creating…' : 'Create'}
                      </Button>
                    </div>
                  )}
                  {realmError && <p className="cp-error">{realmError}</p>}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="ghost" onClick={() => setStep('name')}>Back</Button>
                <Button
                  disabled={!realmId || !canSteward}
                  onClick={() => setStep('covenant')}
                >
                  Continue
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {step === 'covenant' && (
        <div className="cp-form">
          <div className="cp-field">
            <label className="cp-label">Role</label>
            <textarea
              className="cp-textarea"
              placeholder="Describe this familiar's purpose and character. e.g. 'You are a research specialist…'"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              autoFocus
            />
          </div>
          <div className="cp-field">
            <label className="cp-label">Boundaries <span style={{ fontWeight: 400, color: 'var(--ae-color-text-muted)' }}>(one per line, optional)</span></label>
            <textarea
              className="cp-textarea"
              placeholder="Things this familiar will not do."
              value={boundaries}
              onChange={(e) => setBoundaries(e.target.value)}
            />
          </div>
          {error && <p className="cp-error">{error}</p>}
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" onClick={() => setStep(isGuild ? 'realm' : 'name')}>Back</Button>
            <Button disabled={loading} onClick={handleCreate}>
              {loading ? 'Creating…' : `Create ${name}`}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
